import {
    CrossRegionPersistence,
    PersistedLocationInformation,
} from "@brightagency/headstart-common";
import { useCallback, useMemo } from "react";
import { css } from "styled-components";
import { siteData } from "../constants";
import { AcfPostObject, CoordType, SortModes, WPPost } from "../constants/types";
import { ListingAcf, ListingLocation } from "../constants/types/acf";
import { useSortingCoords } from "./hooks";

export const convertToRelativeUrl = (url: string, siteUrl: string = siteData.siteURL) => {
    try {
        const siteUrlWithoutProtocol = siteUrl.replace(new URL(siteUrl).protocol, "");
        // If URL is already relative, do nothing
        if (!url.includes("//")) {
            return url;
        }
        const urlObject = new URL(url);
        const newUrlWithoutProtocol = url.replace(urlObject.protocol, "");
        return newUrlWithoutProtocol.replace(siteUrlWithoutProtocol, "");
    } catch (error) {
        console.error("Error when converting to relative url", { error, url });
        return url;
    }
};

export const getWpPostUrl = (post: WPPost) => {
    return convertToRelativeUrl(post.link);
};

export const getAcfPostUrl = (post: AcfPostObject) => {
    return convertToRelativeUrl(post.link);
};

export const letterSpacing = (spacing: number) => css`
    letter-spacing: ${spacing / 1000}em;
`;

export const buttonResetStyles = css`
    text-align: left;
    width: 100%;
    height: 100%;
    border: 0;
    background: none;
    padding: 0;
    margin: 0;
    cursor: pointer;
    text-decoration: none;

    &:focus {
        outline: none;
    }
`;

export const screenHeight = css`
    height: 100vh;
`;

export const minScreenHeight = css`
    min-height: 100vh;
    min-height: -webkit-fill-available;
`;

export const formatDate = (datestring: string) => {
    const months: Record<string, string> = {
        0: "January",
        1: "February",
        2: "March",
        3: "April",
        4: "May",
        5: "June",
        6: "July",
        7: "August",
        8: "September",
        9: "October",
        10: "November",
        11: "December",
    };

    const date = new Date(datestring);
    const formatted = `${date.getDate()} ${months[date.getMonth()]} ${date.getFullYear()}`;
    return formatted;
};

export const titleToUrl = (title: string) => {
    return title.replace(/\W+/g, "-").toLowerCase();
};

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
export const removeEmptyStringValues = obj => {
    return Object.keys(obj).reduce((total, current) => {
        const value = obj[current];
        if (value) {
            return {
                ...total,
                [current]: value,
            };
        }
        return total;
    }, {});
};

type LatLng = [number, number];

export const haversineDistance = ([lat1, lng1]: LatLng, [lat2, lng2]: LatLng, isMiles = false) => {
    const toRadian = (angle: number) => (Math.PI / 180) * angle;
    const distance = (a: number, b: number) => (Math.PI / 180) * (a - b);
    const RADIUS_OF_EARTH_IN_KM = 6371;

    const dLat = distance(lat2, lat1);
    const dLon = distance(lng2, lng1);

    lat1 = toRadian(lat1);
    lat2 = toRadian(lat2);

    // Haversine Formula
    const a =
        Math.pow(Math.sin(dLat / 2), 2) +
        Math.pow(Math.sin(dLon / 2), 2) * Math.cos(lat1) * Math.cos(lat2);
    const c = 2 * Math.asin(Math.sqrt(a));

    let finalDistance = RADIUS_OF_EARTH_IN_KM * c;

    if (isMiles) {
        finalDistance /= 1.60934;
    }

    return finalDistance;
};

const roundToOneDecimal = (x: number) => Math.round(x * 10) / 10;

export const getListingDistanceFromUser = (
    listing: WPPost<ListingAcf>,
    userLocation: CoordType,
) => {
    const distances = (listing.acf.locations || []).map(loc => {
        const { lat, lng } = loc.address;
        return haversineDistance([lat, lng], [userLocation.lat, userLocation.lng]);
    });
    const distance = Math.min(...distances);

    if (distance) {
        return roundToOneDecimal(distance);
    }
    return undefined;
};

export const getLocationDistanceFromUser = (location: ListingLocation, userLocation: CoordType) => {
    return roundToOneDecimal(
        haversineDistance(
            [location.address.lat, location.address.lng],
            [userLocation.lat, userLocation.lng],
        ),
    );
};

const formatDistanceString = (distance: number, sortMode: SortModes, suburbName: string) => {
    switch (sortMode) {
        case SortModes.DistanceFromSuburb:
            return `${distance}km from ${suburbName}`;
        case SortModes.DistanceFromUser:
            return `${distance}km away`;
        default:
            throw new Error(`Unknown sort mode: ${sortMode}`);
    }
};

export const getListingDistanceFromUserString = (
    listing: WPPost<ListingAcf>,
    userLocation: CoordType,
    sortMode: SortModes,
    suburbName: string,
) => {
    const distance = getListingDistanceFromUser(listing, userLocation);

    if (distance) {
        return formatDistanceString(distance, sortMode, suburbName);
    }
    return undefined;
};

export const getLocationDistanceFromUserString = (
    location: ListingLocation,
    userLocation: CoordType,
    sortMode: SortModes,
    suburbName: string,
) => {
    return formatDistanceString(
        getLocationDistanceFromUser(location, userLocation),
        sortMode,
        suburbName,
    );
};

export function mergeRefs<T>(
    refs: (React.MutableRefObject<T> | ((instance: T) => void) | undefined)[],
) {
    const filteredRefs = refs.filter(Boolean);
    if (!filteredRefs.length) return null;
    if (filteredRefs.length === 0) return filteredRefs[0];

    return (instance: T) => {
        for (const ref of filteredRefs) {
            if (typeof ref === "function") {
                ref(instance);
            } else if (ref) {
                ref.current = instance;
            }
        }
    };
}

const sortStrings = (s1: string, s2: string) => {
    if (s1.toLowerCase() > s2.toLowerCase()) return 1;
    if (s2.toLowerCase() > s1.toLowerCase()) return -1;
    return 0;
};

const sortFeatured = (a: WPPost<ListingAcf>, b: WPPost<ListingAcf>): 1 | -1 | undefined => {
    if (a.acf.meta.featured !== b.acf.meta.featured) {
        if (a.acf.meta.featured) {
            return -1;
        }
        return 1;
    }
    return undefined;
};

export const createListingLocationDistanceSorter = (userCoords: CoordType | undefined) => (
    a: WPPost<ListingAcf>,
    b: WPPost<ListingAcf>,
) => {
    const featuredSort = sortFeatured(a, b);
    if (featuredSort) {
        return featuredSort;
    }
    if (userCoords) {
        const distanceA = getListingDistanceFromUser(a, userCoords) ?? Number.POSITIVE_INFINITY;
        const distanceB = getListingDistanceFromUser(b, userCoords) ?? Number.POSITIVE_INFINITY;
        if (distanceA < distanceB) return -1;
        if (distanceA > distanceB) return 1;
    }
    return sortStrings(a.title.rendered, b.title.rendered);
};

export const useListingLocationDistanceSorter = () => {
    const coords = useSortingCoords();
    const sorter = useMemo(() => createListingLocationDistanceSorter(coords), [coords]);
    return sorter;
};

export const useListingSorters = () => {
    const locationSort = useListingLocationDistanceSorter();

    const nameSort = useCallback((a: WPPost<ListingAcf>, b: WPPost<ListingAcf>) => {
        const featuredSort = sortFeatured(a, b);
        if (featuredSort !== undefined) {
            return featuredSort;
        }
        return sortStrings(a.title.rendered, b.title.rendered);
    }, []);

    return { locationSort, nameSort };
};

export const hasScrollToElementInHash = (hash: string) => {
    if (hash) {
        try {
            const element = document.querySelector(hash);
            if (element) {
                return true;
            }
        } catch {
            //
        }
    }
    return false;
};

export const scrollToElementId = (elId: string) => {
    if (elId) {
        try {
            const element = document.querySelector(elId);
            if (element) {
                element.scrollIntoView({ behavior: "smooth" });
            }
        } catch {
            //
        }
    }
};

export const scrollToElementIdPosition = (elId: string) => {
    if (elId) {
        try {
            const element = document.querySelector(elId);
            if (element) {
                scrollToPosition(element.getBoundingClientRect().y + window.scrollY);
            }
        } catch {
            //
        }
    }
};

const scrollToPosition = function (position: number) {
    const speed = 700;
    const startPosition: number = window.scrollY;
    const distance: number = position - startPosition;
    let startTime: number | null = null;

    function animation(currentTime:number) {
        if (startTime === null) startTime = currentTime;
        const timeElapsed = currentTime - startTime;
        const scrollTop = easeInOutQuad(timeElapsed, startPosition, distance, speed);
        window.scrollTo(0, scrollTop);
        if (timeElapsed < speed) requestAnimationFrame(animation);
    }

    function easeInOutQuad(t: number, b: number, c: number, d: number) {
        t /= d / 2;
        if (t < 1) return (c / 2) * t * t + b;
        t--;
        return (-c / 2) * (t * (t - 2) - 1) + b;
    }

    requestAnimationFrame(animation);
}

const scrollToSmoothly = function (pos:number, time:number) {
    const currentPos = window.scrollY;
    let start: number | null = null;
    if (time == null) time = 500;
    pos = +pos, time = +time;
    window.requestAnimationFrame(function step(currentTime) {
        start = !start ? currentTime : start;
        const progress = currentTime - start;
        if (currentPos < pos) {
            window.scrollTo(0, ((pos - currentPos) * progress / time) + currentPos);
        } else {
            window.scrollTo(0, currentPos - ((currentPos - pos) * progress / time));
        }
        if (progress < time) {
            window.requestAnimationFrame(step);
        } else {
            window.scrollTo(0, pos);
        }
    });
}

export const useSuburbInformationNullable = (): PersistedLocationInformation | undefined => {
    const suburb = CrossRegionPersistence.getChosenSuburb();
    return useMemo(() => suburb, [suburb]);
};

export const useSuburbName = () => {
    const { name } = useSuburbInformationNullable() ?? {};
    return useMemo(() => name, [name]);
};

export const useSuburbCoords = (): CoordType | undefined => {
    const { lat, lng } = useSuburbInformationNullable() ?? {};
    return useMemo(() => (lat && lng ? { lat, lng } : undefined), [lat, lng]);
};
